import React from "react";
import {
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";

const RequiredStar = () => (
  <Typography component="span" color="error" sx={{ ml: 0.5 }}>
    *
  </Typography>
);

const FieldLabel = ({ label, required }) => (
  <Typography
    component="span"
    sx={{
      display: "block",
      mb: 1,
      fontWeight: "medium",
      fontSize: "0.875rem",
    }}
  >
    {label}
    {required && <RequiredStar />}
  </Typography>
);

const ScheduleDetailsForm = ({
  schedule,
  routes,
  vehicles,
  errors,
  handleChange,
  handleVehicleChange,
}) => {
  const { t } = useTranslation();

  console.log(vehicles, "vehicles from naveed");
  console.log(routes, "routes");

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid container spacing={4} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant="outlined" error={!!errors.name}>
            <FieldLabel label={t("Name")} required={true} />
            <TextField
              name="name"
              value={schedule.name}
              onChange={handleChange}
              placeholder={t("scheduleName")}
              required
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              error={!!errors.name}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
            {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant="outlined" error={!!errors.repitation}>
            <FieldLabel label={t("Repetition")} required={true} />
            <Select
              labelId="repetition-label"
              name="repitation"
              value={schedule.repitation}
              onChange={handleChange}
              required
              fullWidth
              displayEmpty
              error={!!errors.repitation}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "10px",
                },
              }}
            >
              <MenuItem value="">{t("Select Repetition")}</MenuItem>
              <MenuItem value="DAILY">{t("Daily")}</MenuItem>
              <MenuItem value="WEEKLY">{t("Weekly")}</MenuItem>
              <MenuItem value="MONTHLY">{t("Monthly")}</MenuItem>
            </Select>
            {errors.repitation && (
              <FormHelperText error>{errors.repitation}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant="outlined" error={!!errors.routeId}>
            <FieldLabel label={t("Select Route")} required={true} />
            <Select
              labelId="route-label"
              name="routeId"
              value={schedule.routeId}
              onChange={handleChange}
              required
              fullWidth
              displayEmpty
              error={!!errors.routeId}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "10px",
                },
              }}
            >
              <MenuItem value="">{t("Select Route")}</MenuItem>
              {routes?.map((route) => (
                <MenuItem key={route.id} value={route.id}>
                  {route.name}
                </MenuItem>
              ))}
            </Select>
            {errors.routeId && (
              <FormHelperText error>{errors.routeId}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant="outlined" error={!!errors.vehicleId}>
            <FieldLabel label={t("Select Vehicle")} required={false} />
            <Select
              labelId="vehicle-label"
              name="vehicleIds"
              value={schedule.vehicleIds}
              onChange={(e) => {
                const selectedOptions = e.target.value.filter(
                  (value) => value !== ""
                ); // Filter out empty string
                handleVehicleChange({
                  target: { name: "vehicleIds", value: selectedOptions },
                });
              }}
              fullWidth
              multiple
              displayEmpty
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "10px",
                },
              }}
            >
              <MenuItem value="" disabled>
                {t("Select Vehicle")}
              </MenuItem>
              {vehicles?.map((vehicle) => (
                <MenuItem key={vehicle.id} value={vehicle.id}>
                  {vehicle.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FieldLabel label={t("Start Time")} required={true} />
          <DateTimePicker
            value={schedule.startTime ? new Date(schedule.startTime) : null}
            onChange={(newValue) =>
              handleChange({
                target: {
                  name: "startTime",
                  value: newValue ? newValue.toISOString() : "",
                },
              })
            }
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!errors.startTime,
                helperText: errors.startTime && errors.startTime,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                },
              },
            }}
          />
          {errors.startTime && (
            <FormHelperText error>{errors.startTime}</FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <FieldLabel label={t("End Time")} required={true} />
          <DateTimePicker
            value={schedule.endTime ? new Date(schedule.endTime) : null}
            onChange={(newValue) =>
              handleChange({
                target: {
                  name: "endTime",
                  value: newValue ? newValue.toISOString() : "",
                },
              })
            }
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!errors.endTime,
                helperText: errors.endTime && errors.endTime,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                },
              },
            }}
          />
          {errors.endTime && (
            <FormHelperText error>{errors.endTime}</FormHelperText>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <FieldLabel label={t("Description")} required={true} />
          <TextField
            name="description"
            value={schedule.description}
            onChange={handleChange}
            required
            fullWidth
            multiline
            rows={4}
            placeholder={t("Enter description here")}
            variant="outlined"
            error={!!errors.description}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
              },
            }}
          />
          {errors.description && (
            <FormHelperText error>{errors.description}</FormHelperText>
          )}
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default ScheduleDetailsForm;